import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Grid, 
  Link,
  Typography, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  IconButton,
  Tooltip,
  InputAdornment,
  alpha,
  Paper,
  Divider,
  Tabs,
  Tab,
  TablePagination
} from '@material-ui/core';
import { motion, AnimatePresence } from 'framer-motion';
import PlusIcon from '../../icons/Plus';
import PencilAltIcon from '../../icons/PencilAlt';
import TrashIcon from '../../icons/Trash';
import EyeIcon from '../../icons/Eye';
import ClipboardListIcon from '../../icons/ClipboardList';
import QuestionnaireImport from '../../components/dashboard/questionnaire/QuestionnaireImport';
import ChevronRightIcon from '../../icons/ChevronRight';
import PaperAirplaneIcon from '../../icons/PaperAirplane';
import DuplicateIcon from '../../icons/Duplicate';
import SearchIcon from '@material-ui/icons/Search';
import QuestionnairePreview from '../../components/dashboard/questionnaire/QuestionnairePreview';
import DescriptionIcon from '@mui/icons-material/Description'; // Forcé pour rafraîchir le dev server
import { formationApi } from '../../api/formationApi';
import { questionnaireApi } from '../../api/questionnaireApi';
import useMounted from '../../hooks/useMounted';
import toast from 'react-hot-toast';
import Scrollbar from '../../components/Scrollbar';
import { getTone, toneAlpha, lightVeil, greyAlpha, grey } from '../../theme/tones';
import useAuth from '../../hooks/useAuth';
import usePermissions from '../../hooks/usePermissions';

// --- EXTRAORDINARY STYLING COMPONENTS ---

const AnimatedMeshBackground = () => (
    <Box sx={{ 
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
        overflow: 'hidden', zIndex: 0, opacity: 0.4,
        background: (theme) => `${theme.palette.background.default}`,
        pointerEvents: 'none'
    }}>
        <motion.div
            animate={{ x: [0, 120, -60, 0], y: [0, -120, 60, 0], scale: [1, 1.3, 0.8, 1] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', top: '-15%', left: '-10%', width: '45vw', height: '45vw', background: `radial-gradient(circle, ${toneAlpha('indigo', 0.12)} 0%, ${lightVeil(0)} 70%)`, borderRadius: '50%', filter: 'blur(60px)' }}
        />
        <motion.div
            animate={{ x: [0, -100, 80, 0], y: [0, 100, -50, 0], scale: [1, 1.2, 0.9, 1] }}
            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', bottom: '-25%', right: '-10%', width: '55vw', height: '55vw', background: `radial-gradient(circle, ${toneAlpha('emerald', 0.1)} 0%, ${lightVeil(0)} 70%)`, borderRadius: '50%', filter: 'blur(60px)' }}
        />
    </Box>
);

// Couleur de la pastille de type ; un type inconnu retombe sur l'orange du Post.
const TYPE_TONES = {
  'Pré-Formation': 'emerald',
  'Post-Formation': 'orange',
  Satisfaction: 'indigo',
  Validation: 'cyan'
};

const QuestionnaireFormation = () => {
  const { can } = usePermissions();
  const { user } = useAuth();
  const [openModal, setOpenModal] = useState(false);
  const [formations, setFormations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  
  const [nom, setNom] = useState('');
  const [type, setType] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [selectedFormation, setSelectedFormation] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [formatFilter, setFormatFilter] = useState('');
  const [professionFilter, setProfessionFilter] = useState('');

  // State pour l'envoi contextuel (Global)
  const [openSendModal, setOpenSendModal] = useState(false);
  const [sendingQuestionnaireId, setSendingQuestionnaireId] = useState(null);
  const [sendFormation, setSendFormation] = useState('');
  const [sendSession, setSendSession] = useState('');
  const [sendSessions, setSendSessions] = useState([]);

  // State pour le preview
  const [openPreview, setOpenPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // State pour l'édition en ligne
  const [editingCell, setEditingCell] = useState(null); 
  const [editValue, setEditValue] = useState('');
  const [editSessions, setEditSessions] = useState([]);
  const [loadingEditSessions, setLoadingEditSessions] = useState(false);
  
  const mounted = useMounted();
  const navigate = useNavigate();

  // Liste allégée : cet écran n'utilise que id, name, format et professions.
  // La fiche complète pèse 1,6 Mo pour rien (cf. formationApi.getFormationOptions).
  const getFormations = useCallback(async () => {
    try {
      const data = await formationApi.getFormationOptions(localStorage.getItem('accessToken'));
      if (mounted.current) setFormations(data || []);
    } catch (err) { console.error(err); }
  }, [mounted]);

  const getQuestionnaires = useCallback(async () => {
    try {
      const response = await questionnaireApi.getQuestionnaires(localStorage.getItem('accessToken'));
      if (mounted.current && response && response.status === 200) {
        setQuestionnaires(response.data);
      }
    } catch (err) { console.error(err); }
  }, [mounted]);

  useEffect(() => {
    getFormations();
    getQuestionnaires();
  }, [getFormations, getQuestionnaires]);

  useEffect(() => {
    const fetchSessions = async () => {
      if (!selectedFormation) {
        setSessions([]);
        setSelectedSession('');
        return;
      }
      try {
        const data = await questionnaireApi.getSessionsByFormationId(selectedFormation, localStorage.getItem('accessToken'));
        if (mounted.current) {
          setSessions(data || []);
          setSelectedSession('');
        }
      } catch (err) { console.error(err); }
    };
    fetchSessions();
  }, [selectedFormation, mounted]);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => {
    setOpenModal(false);
    setNom('');
    setType('');
    setIsGlobal(false);
    setSelectedFormation('');
    setSelectedSession('');
  };

  const handleCreate = async () => {
    if (!nom || !type) {
      toast.error('Veuillez remplir le nom et le type du questionnaire.');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: nom,
        type: type,
        formation_id: isGlobal ? null : (selectedFormation || null),
        session_id: isGlobal ? null : (selectedSession || null)
      };
      const response = await questionnaireApi.createQuestionnaire(payload, localStorage.getItem('accessToken'));
      if (response && response.status === 200) {
        toast.success('Questionnaire créé avec succès !');
        handleClose();
        getQuestionnaires();
      } else {
        toast.error(response.message || 'Une erreur est survenue.');
      }
    } catch (error) {
      toast.error('Erreur lors de la création.');
    } finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if(!window.confirm("Êtes-vous sûr de vouloir supprimer ce questionnaire ?")) return;
    try {
      const response = await questionnaireApi.deleteQuestionnaire(id, localStorage.getItem('accessToken'));
      if(response && response.status === 200) {
        toast.success('Questionnaire supprimé.');
        getQuestionnaires();
      }
    } catch(err) { toast.error('Erreur inattendue.'); }
  };
  
  const handleOpenPreview = async (questionnaire) => {
    setPreviewData(questionnaire);
    setOpenPreview(true);
    setLoadingPreview(true);
    try {
      const accessToken = window.localStorage.getItem('accessToken');
      const response = await questionnaireApi.getQuestions(questionnaire.id, accessToken);
      if (response.status) setPreviewQuestions(response.data);
    } catch (err) { toast.error('Erreur lors du chargement'); }
    finally { setLoadingPreview(false); }
  };

  // Sans session rattachée, l'envoi a besoin d'un contexte : questionnaires
  // globaux, et ceux portés par une formation (validation du certificat de
  // décès), dont la formation est alors pré-sélectionnée.
  const handleOpenSendModal = async (questionnaire) => {
    setSendingQuestionnaireId(questionnaire.id);
    setSendFormation(questionnaire.formation_id || '');
    setSendSession('');
    setSendSessions([]);
    setOpenSendModal(true);
    if (questionnaire.formation_id) {
      const data = await questionnaireApi.getSessionsByFormationId(questionnaire.formation_id, localStorage.getItem('accessToken'));
      setSendSessions(data || []);
    }
  };

  const handleSendEmail = async (id, contextFormationId = null, contextSessionId = null) => {
    const accessToken = window.localStorage.getItem('accessToken');
    const loadToast = toast.loading('Envoi en cours...');
    try {
      const response = await questionnaireApi.sendByEmail(id, accessToken, contextFormationId, contextSessionId);
      if (response.status) {
        toast.success('Questionnaire envoyé !', { id: loadToast });
        setOpenSendModal(false);
      } else {
        toast.error('Erreur lors de l\'envoi.', { id: loadToast });
      }
    } catch (err) { toast.error('Erreur technique.', { id: loadToast }); }
  };

  const handleDuplicate = async (id) => {
    const accessToken = window.localStorage.getItem('accessToken');
    const loadToast = toast.loading('Duplication en cours...');
    try {
      const response = await questionnaireApi.duplicateQuestionnaire(id, accessToken);
      if (response.status) {
        toast.success('Dupliqué !', { id: loadToast });
        getQuestionnaires();
      }
    } catch (err) { toast.error('Erreur technique.', { id: loadToast }); }
  };

  const handleEditStart = async (q, field) => {
    setEditingCell({ id: q.id, field });
    if (field === 'session_id') {
      setEditValue(q.session_id || '');
      // Charger les sessions de la formation associée
      if (q.formation_id) {
        setLoadingEditSessions(true);
        try {
          const data = await questionnaireApi.getSessionsByFormationId(q.formation_id, localStorage.getItem('accessToken'));
          setEditSessions(data || []);
        } catch (err) { setEditSessions([]); }
        finally { setLoadingEditSessions(false); }
      } else {
        setEditSessions([]);
      }
    } else if (field === 'formation_id') {
      setEditValue(q.formation_id ? String(q.formation_id) : '');
    } else {
      setEditValue(q[field] || '');
    }
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
    setEditSessions([]);
  };

  const handleEditSave = async (q, field, forceValue = undefined) => {
    const valueToSave = forceValue !== undefined ? forceValue : editValue;
    const currentValue = field === 'session_id'
      ? (q.session_id || '')
      : field === 'formation_id'
        ? (q.formation_id ? String(q.formation_id) : '')
        : (q[field] || '');
    if (valueToSave === currentValue) {
      handleEditCancel();
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const payload = { ...q, [field]: valueToSave || null };
      // Changer la formation invalide la session précédente (elle appartient à l'ancienne formation)
      if (field === 'formation_id') {
        payload.session_id = null;
      }
      const response = await questionnaireApi.updateQuestionnaire(payload, accessToken);
      if (response && response.status === 200) {
        toast.success('Mis à jour');
        getQuestionnaires();
      }
    } catch (err) { toast.error('Erreur technique'); }
    finally { handleEditCancel(); }
  };

  // Correspondance formation_id -> formation (pour récupérer format / professions)
  const formationById = useMemo(() => {
    const map = {};
    formations.forEach((f) => { map[String(f.id)] = f; });
    return map;
  }, [formations]);

  // Options de la cellule « Formation / Cible » (recherche par saisie)
  const formationOptions = useMemo(() => [
    { id: '', name: 'Global (Toutes formations)' },
    ...formations.map((f) => ({ id: String(f.id), name: f.name || `Formation #${f.id}` })),
  ], [formations]);

  // Options de filtres dérivées des formations liées aux questionnaires
  const formatOptions = useMemo(() => {
    const set = new Set();
    questionnaires.forEach((q) => {
      const f = q.formation_id ? formationById[String(q.formation_id)] : null;
      if (f && f.format && f.format.trim()) set.add(f.format.trim());
    });
    return Array.from(set).sort();
  }, [questionnaires, formationById]);

  const professionOptions = useMemo(() => {
    const set = new Set();
    questionnaires.forEach((q) => {
      const f = q.formation_id ? formationById[String(q.formation_id)] : null;
      if (f && f.professions) {
        f.professions.split(',').forEach((p) => {
          const trimmed = p.trim();
          if (trimmed) set.add(trimmed);
        });
      }
    });
    return Array.from(set).sort();
  }, [questionnaires, formationById]);

  const filteredQuestionnaires = questionnaires.filter((q) => {
    const matchesTab = activeTab === 'All' || q.type === activeTab;
    const matchesSearch = q.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (q.formation_name && q.formation_name.toLowerCase().includes(searchQuery.toLowerCase()));

    const formation = q.formation_id ? formationById[String(q.formation_id)] : null;

    // Filtre Format : les questionnaires globaux (sans formation) passent toujours
    let matchesFormat = true;
    if (formatFilter) {
      matchesFormat = formation ? (formation.format || '') === formatFilter : true;
    }

    // Filtre Profession : la formation peut avoir plusieurs professions (séparées par virgules)
    let matchesProfession = true;
    if (professionFilter) {
      if (formation && formation.professions) {
        const profs = formation.professions.split(',').map((p) => p.trim());
        matchesProfession = profs.includes(professionFilter);
      } else {
        matchesProfession = !formation; // globaux passent, formations sans profession non
      }
    }

    return matchesTab && matchesSearch && matchesFormat && matchesProfession;
  });

  // Pagination du tableau.
  //
  // Les 621 questionnaires étaient tous rendus d'un coup, soit ~5 600 cellules
  // dont chacune porte puces, boutons et champs éditables. On pagine APRÈS les
  // filtres, pour que la recherche et les onglets continuent de porter sur
  // l'ensemble et non sur la seule page affichée.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Un filtre qui réduit la liste peut rendre la page courante vide : on revient
  // au début dès que le nombre de résultats change.
  useEffect(() => { setPage(0); }, [activeTab, searchQuery, formatFilter, professionFilter]);

  const pageQuestionnaires = filteredQuestionnaires.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const stats = {
    total: questionnaires.length,
    formations: [...new Set(questionnaires.filter(q => q.formation_id).map(q => q.formation_id))].length,
    sessions: [...new Set(questionnaires.filter(q => q.session_id).map(q => q.session_id))].length
  };

  return (
    <>
      <Helmet>
        <title>Qualité | Questionnaires | Apteed</title>
      </Helmet>
      
      <Box sx={{ 
          bgcolor: 'action.hover', 
          minHeight: '100%', 
          position: 'relative', 
          overflow: 'hidden',
          pb: 10,
          pt: 4
      }}>
        <AnimatedMeshBackground />

        <Container maxWidth={false} disableGutters sx={{ px: 2, position: 'relative', zIndex: 1 }}>
          
          {/* Header Bento Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
              <Box sx={{ 
                  p: 3, borderRadius: '24px', 
                  background: (theme) => alpha(theme.palette.common.white, 0.8), backdropFilter: 'blur(10px)', border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
                  boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.common.black, 0.03)}`,
                  height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2.5
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.tones.indigo.base} 0%, ${theme.palette.tones.indigo.color} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.tones.indigo.base, 0.3)}`,
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionIcon sx={{ color: grey(0), fontSize: '1.7rem' }} />
                  </Box>
                  <Box>
                    <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />}>
                        <Link color="textPrimary" component={RouterLink} to="/dashboard" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: getTone('indigo').base } }}>Dashboard</Link>
                        <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Qualité</Typography>
                    </Breadcrumbs>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em', mb: 0.5 }}>
                        Gestion des Questionnaires
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Créez et administrez vos questionnaires de formation avec précision.
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        sx={{ maxWidth: 350, flexGrow: 1 }}
                        size="small"
                        placeholder="Rechercher par nom ou formation..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: getTone('indigo').base, fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: '14px', bgcolor: 'white' }
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Format"
                        value={formatFilter}
                        onChange={(e) => setFormatFilter(e.target.value)}
                        variant="outlined"
                        sx={{ minWidth: 170 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ sx: { borderRadius: '14px', bgcolor: 'white' } }}
                    >
                        <MenuItem value="">Tous les formats</MenuItem>
                        {formatOptions.map((f) => ( <MenuItem key={f} value={f}>{f}</MenuItem> ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Profession"
                        value={professionFilter}
                        onChange={(e) => setProfessionFilter(e.target.value)}
                        variant="outlined"
                        sx={{ minWidth: 180 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ sx: { borderRadius: '14px', bgcolor: 'white' } }}
                    >
                        <MenuItem value="">Toutes les professions</MenuItem>
                        {professionOptions.map((p) => ( <MenuItem key={p} value={p}>{p}</MenuItem> ))}
                    </TextField>
                    {/* Modèle CSV et import : même droit que la création,
                        puisque l'import crée des questionnaires. */}
                    {can('questionnaires.manage', user.access_level === 0) && (
                      <QuestionnaireImport onImported={getQuestionnaires} />
                    )}
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<PlusIcon fontSize="small" />}
                        disabled={!can('questionnaires.manage', user.access_level === 0)}
                      onClick={handleOpen}
                        sx={{
                          borderRadius: '14px',
                          px: 3,
                          py: 1,
                          fontWeight: 800,
                          boxShadow: (theme) => `0 10px 20px -5px ${alpha(theme.palette.tones.indigo.base, 0.4)}`,
                          textTransform: 'none'
                        }}
                      >
                        Créer un questionnaire
                    </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
                <Paper sx={{ 
                    p: 3, borderRadius: '24px', height: '100%',
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.text.primary} 100%)`,
                    color: grey(0), position: 'relative', overflow: 'hidden',
                    boxShadow: (theme) => `0 20px 25px -5px ${alpha(theme.palette.text.primary, 0.2)}`,
                    display: 'flex', flexDirection: 'column', justifyContent: 'center'
                }}>
                    <Box sx={{ position: 'absolute', top: -40, right: -40, width: 120, height: 120, background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.tones.indigo.base, 0.3)} 0%, ${alpha(theme.palette.common.black, 0)} 70%)`, borderRadius: '50%' }} />
                    <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5 }}>{stats.total}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Questionnaires</Typography>
                    <Divider sx={{ my: 2, borderColor: (theme) => alpha(theme.palette.common.white, 0.1) }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{stats.formations}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>Formations liées</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{stats.sessions}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>Sessions liées</Typography>
                        </Box>
                    </Box>
                </Paper>
            </Grid>
          </Grid>

          {/* Extraordinary Filter Tabs */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Paper sx={{ 
                p: 0.5, borderRadius: '20px', bgcolor: (theme) => alpha(theme.palette.common.white, 0.6), 
                backdropFilter: 'blur(10px)', border: (theme) => `1px solid ${theme.palette.common.white}`,
                boxShadow: (theme) => `0 4px 10px ${alpha(theme.palette.common.black, 0.02)}`
            }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, val) => setActiveTab(val)}
                    TabIndicatorProps={{ sx: { display: 'none' } }}
                    sx={{ 
                        '& .MuiTabs-flexContainer': { gap: 1 },
                        minHeight: 'auto'
                    }}
                >
                    {[
                        { label: 'Tous', value: 'All' },
                        { label: 'Pré-Formation', value: 'Pré-Formation' },
                        { label: 'Post-Formation', value: 'Post-Formation' },
                        { label: 'Satisfaction', value: 'Satisfaction' },
                        { label: 'Validation', value: 'Validation' }
                    ].map((tab) => (
                        <Tab 
                            key={tab.value}
                            label={tab.label} 
                            value={tab.value}
                            sx={{ 
                                minHeight: 'auto',
                                py: 1, px: 3,
                                borderRadius: '16px',
                                fontWeight: 800,
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                                textTransform: 'none',
                                transition: 'all 0.3s',
                                '&.Mui-selected': {
                                    bgcolor: 'background.paper',
                                    color: getTone('indigo').base,
                                    boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`
                                }
                            }}
                        />
                    ))}
                </Tabs>
            </Paper>
          </Box>

          {/* Table Section */}
          <Card sx={{ 
              borderRadius: '24px', 
              border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.9)}`, 
              background: (theme) => alpha(theme.palette.common.white, 0.7), 
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px 0 ${toneAlpha('blue', 0.05)}`,
              overflow: 'hidden'
          }}>
            <Scrollbar>
              <Box sx={{ minWidth: 900 }}>
                <Table>
                  <TableHead sx={{ bgcolor: greyAlpha(50, 0.5) }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Nom du questionnaire</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Formation / Cible</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Session</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <AnimatePresence mode="popLayout">
                        {pageQuestionnaires.map((q) => (
                            <TableRow 
                                component={motion.tr}
                                layout
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                hover 
                                key={q.id}
                                sx={{ '&:hover': { bgcolor: `${toneAlpha('indigo', 0.02)} !important` } }}
                            >
                            <TableCell onDoubleClick={() => handleEditStart(q, 'name')}>
                                {editingCell?.id === q.id && editingCell?.field === 'name' ? (
                                <TextField
                                    autoFocus
                                    fullWidth
                                    size="small"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleEditSave(q, 'name')}
                                    onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleEditSave(q, 'name');
                                    if (e.key === 'Escape') handleEditCancel();
                                    }}
                                    variant="standard"
                                />
                                ) : (
                                <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.9rem' }}>
                                    {q.name}
                                </Typography>
                                )}
                            </TableCell>
                            <TableCell onDoubleClick={() => handleEditStart(q, 'type')} sx={{ cursor: 'pointer' }}>
                                {editingCell?.id === q.id && editingCell?.field === 'type' ? (
                                <TextField
                                    select
                                    autoFocus
                                    size="small"
                                    value={editValue}
                                    onChange={(e) => {
                                      setEditValue(e.target.value);
                                      handleEditSave(q, 'type', e.target.value);
                                    }}
                                    onBlur={() => handleEditSave(q, 'type')}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') handleEditCancel();
                                    }}
                                    variant="standard"
                                    sx={{ minWidth: 140 }}
                                    SelectProps={{ native: true }}
                                >
                                    <option value="Pré-Formation">Pré-Formation</option>
                                    <option value="Post-Formation">Post-Formation</option>
                                    <option value="Satisfaction">Satisfaction</option>
                                    <option value="Validation">Validation</option>
                                </TextField>
                                ) : (
                                <Tooltip title="Double-cliquez pour modifier" placement="top" arrow>
                                <Box sx={{ 
                                    display: 'inline-flex', px: 1.5, py: 0.5, borderRadius: '8px', 
                                    bgcolor: getTone(TYPE_TONES[q.type] || 'orange').bg,
                                    color: getTone(TYPE_TONES[q.type] || 'orange').base,
                                    fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase',
                                    transition: 'all 0.2s',
                                    '&:hover': { opacity: 0.8, boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.tones.indigo.base, 0.2)}` }
                                }}>
                                    {q.type}
                                </Box>
                                </Tooltip>
                                )}
                            </TableCell>
                            <TableCell onDoubleClick={() => handleEditStart(q, 'formation_id')} sx={{ cursor: 'pointer' }}>
                                {editingCell?.id === q.id && editingCell?.field === 'formation_id' ? (
                                <Autocomplete
                                    openOnFocus
                                    autoHighlight
                                    disableClearable
                                    size="small"
                                    options={formationOptions}
                                    getOptionLabel={(o) => o.name || ''}
                                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                                    value={formationOptions.find((o) => String(o.id) === String(editValue)) || formationOptions[0]}
                                    onChange={(e, option, reason) => {
                                      // Ne sauvegarder que sur un choix explicite : vider le champ
                                      // pour retaper une recherche ne doit rien enregistrer.
                                      if (reason !== 'selectOption' || !option) return;
                                      const val = String(option.id);
                                      setEditValue(val);
                                      handleEditSave(q, 'formation_id', val);
                                    }}
                                    onBlur={() => handleEditCancel()}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') handleEditCancel();
                                    }}
                                    sx={{ minWidth: 280 }}
                                    ListboxProps={{ sx: { maxHeight: 280, fontSize: '0.85rem' } }}
                                    noOptionsText="Aucune formation trouvée"
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        autoFocus
                                        variant="standard"
                                        placeholder="Rechercher une formation..."
                                      />
                                    )}
                                />
                                ) : (
                                <Tooltip title="Double-cliquez pour modifier" placement="top" arrow>
                                <Typography variant="body2" sx={{
                                    color: 'text.secondary', fontWeight: 600,
                                    transition: 'all 0.2s',
                                    '&:hover': { color: getTone('indigo').base }
                                }}>
                                    {q.formation_name || (q.formation_id ? 'Formation inconnue' : 'Global (Toutes formations)')}
                                </Typography>
                                </Tooltip>
                                )}
                            </TableCell>
                            <TableCell onDoubleClick={() => q.formation_id ? handleEditStart(q, 'session_id') : null} sx={{ cursor: q.formation_id ? 'pointer' : 'default' }}>
                                {editingCell?.id === q.id && editingCell?.field === 'session_id' ? (
                                <TextField
                                    select
                                    autoFocus
                                    size="small"
                                    value={editValue}
                                    onChange={(e) => {
                                      setEditValue(e.target.value);
                                      handleEditSave(q, 'session_id', e.target.value);
                                    }}
                                    onBlur={() => handleEditSave(q, 'session_id')}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') handleEditCancel();
                                    }}
                                    variant="standard"
                                    sx={{ minWidth: 160 }}
                                    SelectProps={{ native: true }}
                                    disabled={loadingEditSessions}
                                >
                                    <option value="">{loadingEditSessions ? 'Chargement...' : '-- Toutes sessions --'}</option>
                                    {editSessions.map((s) => (
                                      <option key={s.id} value={s.id}>Session N° {s.number}</option>
                                    ))}
                                </TextField>
                                ) : (
                                <Tooltip title={q.formation_id ? 'Double-cliquez pour modifier' : 'Assignez une formation d\'abord'} placement="top" arrow>
                                <Typography variant="caption" sx={{ 
                                    fontWeight: 700, color: 'text.secondary',
                                    transition: 'all 0.2s',
                                    '&:hover': q.formation_id ? { color: getTone('indigo').base } : {}
                                }}>
                                    {q.session_name ? `S${q.session_name}` : (q.formation_id ? 'Toutes sessions' : '-')}
                                </Typography>
                                </Tooltip>
                                )}
                            </TableCell>
                            <TableCell align="center">
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                <Tooltip title="Aperçu">
                                    <IconButton size="small" onClick={() => handleOpenPreview(q)} sx={{ color: 'text.secondary' }}>
                                    <EyeIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Envoyer">
                                    <IconButton size="small" onClick={() => (q.session_id ? handleSendEmail(q.id) : handleOpenSendModal(q))} sx={{ color: 'text.primary' }}>
                                    <PaperAirplaneIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Dupliquer">
                                    <IconButton size="small" onClick={() => handleDuplicate(q.id)} sx={{ color: getTone('indigo').base }}>
                                    <DuplicateIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Éditer">
                                    <IconButton size="small" component={RouterLink} to={`/dashboard/questionnaire-formation/${q.id}`} sx={{ color: getTone('indigo').color }}>
                                    <PencilAltIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                    <IconButton size="small" onClick={() => handleDelete(q.id)} sx={{ color: 'error.main' }}>
                                    <TrashIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                </Box>
                            </TableCell>
                            </TableRow>
                        ))}
                    </AnimatePresence>
                    {filteredQuestionnaires.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                          <Box sx={{ opacity: 0.5 }}>
                            <SearchIcon sx={{ fontSize: 48, mb: 2, color: 'text.disabled' }} />
                            <Typography variant="h6" sx={{ color: 'text.disabled', fontWeight: 800 }}>Aucun résultat</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Scrollbar>
            {filteredQuestionnaires.length > 0 && (
              <TablePagination
                component="div"
                count={filteredQuestionnaires.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage="Lignes par page"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
              />
            )}
          </Card>
        </Container>

        {/* --- DIALOGS (Kept functionality, updated style) --- */}
        <Dialog open={openModal} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
          <DialogTitle sx={{ fontWeight: 900, color: 'text.primary' }}>Créer un nouveau questionnaire</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <TextField fullWidth label="Nom du questionnaire" variant="outlined" margin="normal" value={nom} onChange={(e) => setNom(e.target.value)} InputProps={{ sx: { borderRadius: '12px' } }} />
              <TextField select fullWidth label="Type du questionnaire" variant="outlined" margin="normal" value={type} onChange={(e) => setType(e.target.value)} InputProps={{ sx: { borderRadius: '12px' } }}>
                <MenuItem value="Pré-Formation">Pré-Formation</MenuItem>
                <MenuItem value="Post-Formation">Post-Formation</MenuItem>
                <MenuItem value="Satisfaction">Questionnaire de satisfaction</MenuItem>
                <MenuItem value="Validation">Questionnaire de validation</MenuItem>
              </TextField>
              <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', p: 2, bgcolor: 'action.hover', borderRadius: '12px' }}>
                <Typography variant="body2" sx={{ mr: 2, fontWeight: 700, color: 'text.secondary' }}>Est-il global (toutes formations) ?</Typography>
                <TextField select size="small" value={isGlobal ? 'yes' : 'no'} onChange={(e) => setIsGlobal(e.target.value === 'yes')} variant="outlined" sx={{ width: 100 }} SelectProps={{ native: true }} InputProps={{ sx: { borderRadius: '8px' } }}>
                    <option value="no">Non</option>
                    <option value="yes">Oui</option>
                </TextField>
              </Box>
              {!isGlobal && (
                <>
                  <TextField select fullWidth label="Sélectionner une formation" variant="outlined" margin="normal" value={selectedFormation} onChange={(e) => setSelectedFormation(e.target.value)} InputProps={{ sx: { borderRadius: '12px' } }}>
                    <MenuItem value="">-- Choisir une formation --</MenuItem>
                    {formations.map((f) => ( <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem> ))}
                  </TextField>
                  {selectedFormation && (
                    <TextField select fullWidth label="Sélectionner une session (Optionnel)" variant="outlined" margin="normal" value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)} InputProps={{ sx: { borderRadius: '12px' } }}>
                      <MenuItem value="">-- Toutes les sessions --</MenuItem>
                      {sessions.map((s) => ( <MenuItem key={s.id} value={s.id}>Session N° {s.number}</MenuItem> ))}
                    </TextField>
                  )}
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleClose} sx={{ color: 'text.secondary', fontWeight: 800 }}>Annuler</Button>
            <Button onClick={handleCreate} color="primary" variant="contained" disabled={isSubmitting} sx={{ borderRadius: '12px', px: 4, fontWeight: 800 }}>Créer</Button>
          </DialogActions>
        </Dialog>

        {/* Modal d'envoi contextuel */}
        <Dialog open={openSendModal} onClose={() => setOpenSendModal(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
          <DialogTitle sx={{ fontWeight: 900 }}>Envoi contextuel</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>Choisissez la formation et la session visées par cet envoi.</Typography>
            <TextField select fullWidth label="Formation" variant="outlined" margin="normal" value={sendFormation} onChange={async (e) => {
                const fId = e.target.value; setSendFormation(fId); setSendSession('');
                if (fId) { const data = await questionnaireApi.getSessionsByFormationId(fId, localStorage.getItem('accessToken')); setSendSessions(data || []); }
                else setSendSessions([]);
              }} InputProps={{ sx: { borderRadius: '12px' } }}>
              <MenuItem value="">-- Choisir une formation --</MenuItem>
              {formations.map((f) => ( <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem> ))}
            </TextField>
            <TextField select fullWidth label="Session" variant="outlined" margin="normal" value={sendSession} onChange={(e) => setSendSession(e.target.value)} disabled={!sendFormation} InputProps={{ sx: { borderRadius: '12px' } }}>
              <MenuItem value="">-- Choisir une session --</MenuItem>
              {sendSessions.map((s) => ( <MenuItem key={s.id} value={s.id}>Session N° {s.number}</MenuItem> ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenSendModal(false)} sx={{ fontWeight: 800 }}>Annuler</Button>
            <Button variant="contained" color="primary" disabled={!sendSession} onClick={() => handleSendEmail(sendingQuestionnaireId, sendFormation, sendSession)} sx={{ borderRadius: '12px', fontWeight: 800 }}>Envoyer</Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Preview */}
        <Dialog open={openPreview} onClose={() => setOpenPreview(false)} fullWidth maxWidth="md" scroll="body" PaperProps={{ sx: { borderRadius: '24px' } }}>
          <DialogTitle sx={{ backgroundColor: 'action.hover', pb: 0, pt: 2 }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Aperçu du questionnaire</Typography>
                <Button onClick={() => setOpenPreview(false)} sx={{ color: 'text.secondary' }}>Fermer</Button>
             </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, backgroundColor: 'action.hover' }}>
            {loadingPreview ? ( <Box sx={{ p: 4, textAlign: 'center' }}>Chargement...</Box> ) : (
              <QuestionnairePreview questionnaire={previewData} questions={previewQuestions} />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};

export default QuestionnaireFormation;
